import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, ArrowRight, Check, Film, Music, Palette, FileText, Loader, Trash2,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassSelect } from '../ui';
import {
  parseScript, createProduction,
  getMusicPresets, getColorGrades,
  getScripts,
  ParsedScene, MusicPreset, ColorGrade, Script,
} from '../../services/api';
import './VideoProduction.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Props {
  onComplete: () => void;
  onCancel: () => void;
}

type Step = 'source' | 'scenes' | 'style' | 'confirm';

const ASPECT_OPTIONS = [
  { value: '16:9', label: 'Landscape 16:9 (YouTube, LinkedIn)' },
  { value: '9:16', label: 'Portrait 9:16 (Reels, TikTok, Stories)' },
  { value: '1:1',  label: 'Square 1:1 (Instagram Feed)' },
];

const DURATION_OPTIONS = [
  { value: '5',  label: '5 seconds per scene' },
  { value: '10', label: '10 seconds per scene' },
];

const ProductionWizard: React.FC<Props> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<Step>('source');

  // Step 1 state
  const [scripts, setScripts] = useState<Script[]>([]);
  const [selectedScriptId, setSelectedScriptId] = useState('');
  const [rawText, setRawText] = useState('');
  const [sourceMode, setSourceMode] = useState<'script' | 'text'>('script');

  // Step 2 state
  const [scenes, setScenes] = useState<ParsedScene[]>([]);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [targetDuration, setTargetDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState('16:9');

  // Step 3 state
  const [title, setTitle] = useState('');
  const [musicPresets, setMusicPresets] = useState<MusicPreset[]>([]);
  const [colorGrades, setColorGrades] = useState<ColorGrade[]>([]);
  const [musicKey, setMusicKey] = useState('none');
  const [colorGrade, setColorGrade] = useState('none');

  // Step 4 state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    getScripts().then(setScripts).catch(() => {});
    getMusicPresets().then(setMusicPresets).catch(() => {});
    getColorGrades().then(setColorGrades).catch(() => {});
  }, []);

  // ── Step 1: Parse script ──────────────────────────────────────────────────
  const handleParse = async () => {
    setParsing(true);
    setParseError('');
    try {
      const req: { script_id?: string; script_text?: string; target_scene_duration: number; aspect_ratio: string } = {
        target_scene_duration: targetDuration,
        aspect_ratio: aspectRatio,
      };
      if (sourceMode === 'script' && selectedScriptId) {
        req.script_id = selectedScriptId;
        const s = scripts.find(sc => sc.script_id === selectedScriptId);
        if (s && !title) setTitle(s.title || '');
      } else {
        req.script_text = rawText;
      }
      const res = await parseScript(req);
      setScenes(res.scenes);
      setStep('scenes');
    } catch (e: any) {
      setParseError(getApiError(e, 'Failed to parse script'));
    } finally {
      setParsing(false);
    }
  };

  // ── Step 2: Edit scenes ───────────────────────────────────────────────────
  const updateScene = (index: number, field: keyof ParsedScene, value: string | number) => {
    setScenes(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const deleteScene = (index: number) => {
    setScenes(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, i) => ({ ...s, scene_index: i }));
    });
  };

  const totalDuration = scenes.reduce((acc, s) => acc + s.duration, 0);

  // ── Step 4: Submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await createProduction({
        title,
        scenes,
        aspect_ratio: aspectRatio,
        music_track_key: musicKey,
        color_grade: colorGrade,
        ...(sourceMode === 'script' && selectedScriptId ? { script_id: selectedScriptId } : {}),
      });
      onComplete();
    } catch (e: any) {
      setSubmitError(getApiError(e, 'Failed to create production'));
      setSubmitting(false);
    }
  };

  // ── Render steps ─────────────────────────────────────────────────────────

  const stepLabels: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: 'source',  label: 'Source',   icon: <FileText size={16} /> },
    { key: 'scenes',  label: 'Scenes',   icon: <Film size={16} /> },
    { key: 'style',   label: 'Style',    icon: <Palette size={16} /> },
    { key: 'confirm', label: 'Generate', icon: <Check size={16} /> },
  ];

  const stepIndex = stepLabels.findIndex(s => s.key === step);

  return (
    <div className="vp-wizard-root">
      {/* Step indicator */}
      <div className="vp-step-bar">
        {stepLabels.map((s, i) => (
          <div key={s.key} className={`vp-step-item ${i <= stepIndex ? 'active' : ''} ${s.key === step ? 'current' : ''}`}>
            <div className="vp-step-icon">{s.icon}</div>
            <span className="vp-step-label">{s.label}</span>
            {i < stepLabels.length - 1 && <div className="vp-step-connector" />}
          </div>
        ))}
      </div>

      {/* Step 1: Source */}
      {step === 'source' && (
        <GlassCard className="vp-wizard-card">
          <h3 className="vp-wizard-heading">Choose Script Source</h3>

          <div className="vp-source-tabs">
            <button className={`vp-source-tab ${sourceMode === 'script' ? 'active' : ''}`} onClick={() => setSourceMode('script')}>
              From Script Library
            </button>
            <button className={`vp-source-tab ${sourceMode === 'text' ? 'active' : ''}`} onClick={() => setSourceMode('text')}>
              Paste Script
            </button>
          </div>

          {sourceMode === 'script' && (
            <GlassSelect
              label="Select Script"
              value={selectedScriptId}
              onChange={val => setSelectedScriptId(val)}
              options={[
                { value: '', label: 'Choose a script…' },
                ...scripts.map(s => ({ value: s.script_id, label: s.title })),
              ]}
            />
          )}

          {sourceMode === 'text' && (
            <div className="vp-field">
              <label className="vp-label">Script Text</label>
              <textarea
                className="vp-textarea"
                rows={10}
                placeholder="Paste your full script here…"
                value={rawText}
                onChange={e => setRawText(e.target.value)}
              />
            </div>
          )}

          <div className="vp-row-2">
            <GlassSelect
              label="Scene Duration Target"
              value={String(targetDuration)}
              onChange={val => setTargetDuration(Number(val))}
              options={DURATION_OPTIONS}
            />
            <GlassSelect
              label="Aspect Ratio"
              value={aspectRatio}
              onChange={val => setAspectRatio(val)}
              options={ASPECT_OPTIONS}
            />
          </div>

          {parseError && <p className="vp-error-text">{parseError}</p>}

          <div className="vp-wizard-footer">
            <GlassButton variant="secondary" onClick={onCancel}>Cancel</GlassButton>
            <GlassButton
              variant="primary"
              icon={parsing ? <Loader size={16} className="spin" /> : <ArrowRight size={16} />}
              iconPosition="right"
              onClick={handleParse}
              disabled={parsing || (sourceMode === 'script' ? !selectedScriptId : !rawText.trim())}
            >
              {parsing ? 'Parsing…' : 'Parse Script'}
            </GlassButton>
          </div>
        </GlassCard>
      )}

      {/* Step 2: Review Scenes */}
      {step === 'scenes' && (
        <GlassCard className="vp-wizard-card">
          <div className="vp-scenes-header">
            <h3 className="vp-wizard-heading">Review Scenes</h3>
            <span className="vp-scenes-meta">{scenes.length} scenes · ~{Math.round(totalDuration)}s total</span>
          </div>

          <div className="vp-scenes-list">
            {scenes.map((scene, i) => (
              <div key={i} className="vp-scene-row">
                <div className="vp-scene-num">{i + 1}</div>
                <div className="vp-scene-body">
                  <input
                    className="vp-scene-label-input"
                    value={scene.scene_label || `Scene ${i + 1}`}
                    onChange={e => updateScene(i, 'scene_label', e.target.value)}
                    placeholder="Scene label…"
                  />
                  <textarea
                    className="vp-scene-prompt"
                    rows={2}
                    value={scene.prompt}
                    onChange={e => updateScene(i, 'prompt', e.target.value)}
                    placeholder="Visual prompt…"
                  />
                  <input
                    className="vp-scene-hint"
                    value={scene.visual_hint || ''}
                    onChange={e => updateScene(i, 'visual_hint', e.target.value)}
                    placeholder="Visual hint (camera/style)…"
                  />
                </div>
                <div className="vp-scene-controls">
                  <select
                    className="vp-scene-dur"
                    value={scene.duration}
                    onChange={e => updateScene(i, 'duration', Number(e.target.value))}
                  >
                    <option value={5}>5s</option>
                    <option value={10}>10s</option>
                  </select>
                  <button className="vp-scene-del" onClick={() => deleteScene(i)} title="Remove scene">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="vp-wizard-footer">
            <GlassButton variant="secondary" icon={<ArrowLeft size={16} />} onClick={() => { setStep('source'); setParseError(''); }}>
              Re-parse
            </GlassButton>
            <GlassButton variant="primary" icon={<ArrowRight size={16} />} iconPosition="right" onClick={() => setStep('style')} disabled={scenes.length === 0}>
              Next: Style
            </GlassButton>
          </div>
        </GlassCard>
      )}

      {/* Step 3: Style */}
      {step === 'style' && (
        <GlassCard className="vp-wizard-card">
          <h3 className="vp-wizard-heading">Production Style</h3>

          <div className="vp-field">
            <label className="vp-label">Production Title</label>
            <input
              className="vp-title-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Capital Five — Brand Story"
            />
          </div>

          <div className="vp-field">
            <label className="vp-label">Background Music</label>
            <div className="vp-music-grid">
              {musicPresets.map(m => (
                <div
                  key={m.key}
                  className={`vp-music-card ${musicKey === m.key ? 'selected' : ''}`}
                  onClick={() => setMusicKey(m.key)}
                >
                  <Music size={16} />
                  <span className="vp-music-label">{m.label}</span>
                  {m.mood !== 'none' && <span className="vp-music-mood">{m.mood}</span>}
                  {m.bpm && <span className="vp-music-bpm">{m.bpm} BPM</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="vp-field">
            <label className="vp-label">Colour Grade</label>
            <div className="vp-grade-grid">
              {colorGrades.map(g => (
                <div
                  key={g.key}
                  className={`vp-grade-card vp-grade-${g.key} ${colorGrade === g.key ? 'selected' : ''}`}
                  onClick={() => setColorGrade(g.key)}
                >
                  {g.label}
                </div>
              ))}
            </div>
          </div>

          <div className="vp-wizard-footer">
            <GlassButton variant="secondary" icon={<ArrowLeft size={16} />} onClick={() => setStep('scenes')}>
              Back
            </GlassButton>
            <GlassButton variant="primary" icon={<ArrowRight size={16} />} iconPosition="right" onClick={() => setStep('confirm')} disabled={!title.trim()}>
              Next
            </GlassButton>
          </div>
        </GlassCard>
      )}

      {/* Step 4: Confirm & Generate */}
      {step === 'confirm' && (
        <GlassCard className="vp-wizard-card">
          <h3 className="vp-wizard-heading">Ready to Generate</h3>

          <div className="vp-confirm-summary">
            <div className="vp-confirm-row"><span>Title</span><strong>{title}</strong></div>
            <div className="vp-confirm-row"><span>Scenes</span><strong>{scenes.length} scenes</strong></div>
            <div className="vp-confirm-row"><span>Estimated footage</span><strong>~{Math.round(totalDuration)}s</strong></div>
            <div className="vp-confirm-row"><span>Aspect ratio</span><strong>{aspectRatio}</strong></div>
            <div className="vp-confirm-row">
              <span>Music</span>
              <strong>{musicPresets.find(m => m.key === musicKey)?.label || 'No Music'}</strong>
            </div>
            <div className="vp-confirm-row">
              <span>Colour grade</span>
              <strong>{colorGrades.find(g => g.key === colorGrade)?.label || 'None'}</strong>
            </div>
          </div>

          <p className="vp-confirm-note">
            Each scene will be generated individually, then all clips will be assembled into a final video in the cloud. This may take 5–15 minutes depending on your provider.
          </p>

          {submitError && <p className="vp-error-text">{submitError}</p>}

          <div className="vp-wizard-footer">
            <GlassButton variant="secondary" icon={<ArrowLeft size={16} />} onClick={() => setStep('style')} disabled={submitting}>
              Back
            </GlassButton>
            <GlassButton
              variant="primary"
              icon={submitting ? <Loader size={16} className="spin" /> : <Film size={16} />}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Confirm & Generate'}
            </GlassButton>
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default ProductionWizard;
